import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    alpha,
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import {
    confirmSummerStatsMatches,
    UnmatchedSummerPlayer,
    BaseballRecruitProfile,
} from 'api/BaseballRecruits';
import { useSnackbar } from 'notistack';
import useTheme from '@mui/system/useTheme';

interface SummerStatsMatchReviewProps {
    open: boolean;
    onClose: () => void;
    unmatchedPlayers: UnmatchedSummerPlayer[];
    profiles: BaseballRecruitProfile[];
    groupUuid: string;
    brUrl: string;
    onConfirmed: () => void;
}

function getProfileDisplayName(p: BaseballRecruitProfile): string {
    if (p.player_bio) {
        const bio = p.player_bio as Record<string, any>;
        const name = `${bio.first_name || ''} ${bio.last_name || ''}`.trim();
        if (name) return name;
    }
    if (p.scraped_data) {
        const sd = typeof p.scraped_data === 'string' ? JSON.parse(p.scraped_data) : p.scraped_data;
        const player = sd?.player;
        if (player) {
            const name = `${player.first_name || ''} ${player.last_name || ''}`.trim();
            if (name) return name;
        }
    }
    if (p.individual?.name) return p.individual.name;
    return `Profile ${p.uuid.slice(0, 8)}`;
}

const SummerStatsMatchReview: React.FC<SummerStatsMatchReviewProps> = ({
    open,
    onClose,
    unmatchedPlayers,
    profiles,
    groupUuid,
    brUrl,
    onConfirmed,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const [confirming, setConfirming] = useState(false);
    const [selections, setSelections] = useState<Record<number, string>>(() => {
        const initial: Record<number, string> = {};
        unmatchedPlayers.forEach((player, idx) => {
            if (player.suggestions?.length && player.suggestions[0].score >= 0.6) {
                initial[idx] = player.suggestions[0].profile_uuid;
            }
        });
        return initial;
    });

    const profileOptions = useMemo(() => {
        return profiles.map((p) => ({
            uuid: p.uuid,
            name: getProfileDisplayName(p),
            hasSummerStats: !!p.summer_league_data,
        }));
    }, [profiles]);

    const assignedCount = Object.keys(selections).length;

    const handleConfirm = async () => {
        const matches = Object.entries(selections)
            .filter(([, uuid]) => uuid)
            .map(([idx, profileUuid]) => ({
                profile_uuid: profileUuid,
                summer_data: unmatchedPlayers[Number(idx)].summer_data,
            }));

        if (matches.length === 0) {
            enqueueSnackbar('No matches selected', { variant: 'warning' });
            return;
        }

        setConfirming(true);
        try {
            const response = await confirmSummerStatsMatches(groupUuid, brUrl, matches);
            enqueueSnackbar(response.data.message, { variant: 'success' });
            onConfirmed();
            onClose();
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to confirm matches', {
                variant: 'error',
            });
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Match Summer League Stats</Typography>
                    <IconButton onClick={onClose} disabled={confirming}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        This player from Baseball Reference could not be automatically matched to an
                        existing profile. Select which profile they correspond to, or skip.
                    </Typography>
                </Alert>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>BR Player</TableCell>
                                <TableCell sx={{ fontWeight: 700, minWidth: 250 }}>
                                    Assign to Profile
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Suggestion</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {unmatchedPlayers.map((player, idx) => {
                                const topSuggestion = player.suggestions?.[0];
                                return (
                                    <TableRow
                                        key={idx}
                                        sx={
                                            selections[idx]
                                                ? {
                                                      bgcolor: alpha(
                                                          theme.palette.success.main,
                                                          0.05,
                                                      ),
                                                  }
                                                : {}
                                        }
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {player.br_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={selections[idx] || ''}
                                                    onChange={(e) =>
                                                        setSelections((prev) => ({
                                                            ...prev,
                                                            [idx]: e.target.value as string,
                                                        }))
                                                    }
                                                    displayEmpty
                                                    sx={{ fontSize: '0.85rem' }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Skip (no match)</em>
                                                    </MenuItem>
                                                    {profileOptions.map((opt) => (
                                                        <MenuItem key={opt.uuid} value={opt.uuid}>
                                                            {opt.name}
                                                            {opt.hasSummerStats && (
                                                                <Chip
                                                                    label="has stats"
                                                                    size="small"
                                                                    sx={{
                                                                        ml: 1,
                                                                        height: 18,
                                                                        fontSize: '0.65rem',
                                                                    }}
                                                                />
                                                            )}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            {topSuggestion ? (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Chip
                                                        label={`${(
                                                            topSuggestion.score * 100
                                                        ).toFixed(0)}%`}
                                                        size="small"
                                                        color={
                                                            topSuggestion.score >= 0.8
                                                                ? 'success'
                                                                : topSuggestion.score >= 0.6
                                                                ? 'warning'
                                                                : 'default'
                                                        }
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {topSuggestion.name}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    No close match
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={confirming} color="inherit">
                    Skip All
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={confirming || assignedCount === 0}
                    startIcon={confirming ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                    sx={{
                        backgroundColor: '#00897b',
                        '&:hover': { backgroundColor: '#00695c' },
                    }}
                >
                    {confirming
                        ? 'Saving...'
                        : `Confirm ${assignedCount} Match${assignedCount !== 1 ? 'es' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SummerStatsMatchReview;
